import type { Record } from "~/tools/record";

type MatchablePattern<TName extends string, TInstance, TResult> =
	| Record<TName, (instance: TInstance) => TResult>
	| Record<"_", () => TResult>;

function isNamedPattern<TName extends string, TInstance, TResult>(
	name: TName,
	pattern: MatchablePattern<TName, TInstance, TResult>,
): pattern is Record<TName, (instance: TInstance) => TResult> {
	return name in pattern;
}

/**
 * Base class for sum-type variants with built-in exhaustive pattern matching (catamorphism).
 *
 * Subclasses define a `__name` tag and inherit {@link match} automatically.
 * When matched, the named handler receives the subclass instance (`this`).
 *
 * @template TName - The variant name used as the pattern key.
 */
abstract class Matchable<const TName extends string> {
	protected abstract readonly __name: TName;

	/**
	 * Pattern-matches on the current variant, invoking the corresponding handler.
	 *
	 * The {@link pattern} object must provide either a named handler for this variant
	 * or a wildcard `_` handler as a catch-all fallback.
	 *
	 * @param pattern - An object with a named handler or a wildcard `_` handler.
	 * @returns The result of the matched handler.
	 */
	public match<TResult>(pattern: MatchablePattern<TName, this, TResult>): TResult {
		if (isNamedPattern(this.__name, pattern)) {
			return pattern[this.__name](this);
		}

		return pattern._();
	}
}

export { Matchable };
export type { MatchablePattern };
